/**
 * Detect emails that are almost certainly OTA-forwarded aliases.
 *
 * Booking.com, Expedia and the rest issue per-reservation forwarding aliases
 * on domains they own. Emails to those aliases route through the OTA's mail
 * infra, which often silently drops HTML, blocks unauthenticated links, or
 * simply never reaches the guest. If we send the magic link to an alias and
 * the guest DID reach us anyway (a receptionist forwarded, etc.), it's still
 * worth prompting them for their real email so future comms — arrival
 * instructions, invoice, upsell — don't loop through the OTA.
 *
 * Kept as a static suffix list rather than a regex on the local-part —
 * BookingSuite's aliases look like `abcd1234@guest.booking.com`, Expedia's
 * like `xyz-abc@e.mail.expedia.com`. The local-part is high-entropy noise
 * across both, whereas the domain is the reliable signal.
 *
 * Sources: BookingSuite guest-messaging docs (2024), Expedia Partner Central
 * "how we deliver messages" (2024), and hospitality-industry postmortems on
 * OTA email deliverability (StayNTouch blog, Cloudbeds forum).
 */
const OTA_ALIAS_SUFFIXES = [
  "guest.booking.com",
  "message.booking.com",
  "reply.booking.com",
  "m.expediapartnercentral.com",
  "mail.expedia.com",
  "e.mail.expedia.com",
  "guest.airbnb.com",
  "reply.airbnb.com",
  "guest.hotelbeds.com",
  "email.hotels.com",
  "message.hotels.com",
  "guest.agoda.com",
  "message.agoda.com",
  "email.trivago.com",
  "guest.priceline.com",
  "hotelmessage.priceline.com",
  "email.vrbo.com",
  "guest.vrbo.com",
];

export function isLikelyOtaAlias(email: string | null | undefined): boolean {
  if (!email || typeof email !== "string") return false;
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain) return false;
  return OTA_ALIAS_SUFFIXES.some((s) => domain === s || domain.endsWith("." + s));
}
